/**
 * PWA update state and Preferences version UI.
 *
 * The inline script in index.html sets window.__pendingUpdate and dispatches
 * 'counterpunch:update-available' when a new service worker activates.
 * Scheduled checks compare the running EDITOR_VERSION to the published
 * service-worker file and GitHub releases so the Preferences gear can notify
 * even before a new worker activates.
 */
import { Logger } from './logger';
const console = new Logger('UpdateManager');
const CHANGELOG_RELEASES_URL = 'https://github.com/counterpunchspace/editor/releases/tag';
export const AUTO_CHECK_THROTTLE_MS = 60 * 1000;
export const AUTO_CHECK_INTERVAL_MS = 10 * 60 * 1000;
export const FOCUS_POLL_MS = 1000;
let lastAutoCheckAt = 0;
let checkInFlight = false;
let scheduledChecksInitialized = false;
let preferencesUiInitialized = false;
let forceUpdateOffered = false;
let forceReinstallInFlight = false;
export const UPDATE_QUERY = 'update';
const UPDATE_RELOAD_MARKER = /^\d{13}$/;
export function getPendingUpdate() {
    return window.__pendingUpdate ?? null;
}
export function clearPendingUpdate() {
    window.__pendingUpdate = null;
}
const VERSION_ASSIGN_RE = /\bVERSION\s*=\s*['"](v[^'"]+)['"]/;
const DISPLAY_VERSION_ASSIGN_RE = /\bDISPLAY_VERSION\s*=\s*['"]([^'"]+)['"]/;
const GITHUB_RELEASES_URL = 'https://api.github.com/repos/counterpunchspace/editor/releases?per_page=15';
export function isPreviewReleaseId(tag, displayVersion = tag) {
    return (/preview|-pre\./.test(tag) ||
        /preview|-pre\./.test(displayVersion) ||
        /^\d+-build-\d+$/.test(displayVersion));
}
export function parseSwVersions(text) {
    const versionMatch = text.match(VERSION_ASSIGN_RE);
    if (!versionMatch?.[1]) {
        return null;
    }
    const displayMatch = text.match(DISPLAY_VERSION_ASSIGN_RE);
    const tag = versionMatch[1];
    const displayVersion = displayMatch?.[1] || tag;
    return {
        tag,
        displayVersion,
        isPreview: isPreviewReleaseId(tag, displayVersion)
    };
}
export function shouldShowForceUpdate(pending, offeredAfterManualCheck) {
    return !pending && offeredAfterManualCheck;
}
/**
 * `?update` (any value except a 13-digit reload marker) starts a force
 * reinstall. After caches are cleared the page reloads with
 * `?update=<Date.now()>`; that marker is stripped and must not reinstall.
 */
export function shouldForceReinstallFromUrl(search = typeof window !== 'undefined' ? window.location.search : '') {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    if (!params.has(UPDATE_QUERY)) {
        return false;
    }
    const value = params.get(UPDATE_QUERY) || '';
    return !UPDATE_RELOAD_MARKER.test(value);
}
export function isAppWindowActive(visibilityState = document.visibilityState, hasFocus = document.hasFocus()) {
    return visibilityState === 'visible' && hasFocus;
}
export function didAppWindowBecomeActive(wasActive, isActive) {
    return isActive && !wasActive;
}
export function hasAvailableUpdate(published, runningDisplayVersion, runningTag = null) {
    const runningIds = [runningDisplayVersion, runningTag].filter((value) => !!value && value !== 'Unknown');
    if (runningIds.length === 0) {
        return false;
    }
    return !runningIds.some((id) => id === published.displayVersion || id === published.tag);
}
export function changelogUrlForUpdate(pending) {
    const releaseTag = pending.tag || pending.version;
    return `${CHANGELOG_RELEASES_URL}/${releaseTag}`;
}
export function pendingUpdateFromSw(info) {
    return {
        version: info.displayVersion,
        tag: info.tag,
        isPreview: info.isPreview
    };
}
function getRunningDisplayVersion() {
    if (window.EDITOR_VERSION) {
        return window.EDITOR_VERSION;
    }
    const versionSpan = document.getElementById('app-version');
    const displayVersion = versionSpan?.dataset.displayVersion;
    if (displayVersion) {
        return displayVersion;
    }
    return versionSpan?.dataset.version || 'Unknown';
}
function getRunningTag() {
    const running = window.EDITOR_VERSION;
    if (running?.startsWith('v')) {
        return running;
    }
    return null;
}
function setPendingUpdate(pending) {
    window.__pendingUpdate = pending;
    document.dispatchEvent(new CustomEvent('counterpunch:update-available', {
        detail: pending
    }));
}
function setHidden(element, hidden) {
    if (!element) {
        return;
    }
    element.hidden = hidden;
}
function refreshPreferencesVersionUi() {
    const versionEl = document.getElementById('settings-current-version');
    if (versionEl) {
        versionEl.textContent = getRunningDisplayVersion();
    }
    const pending = getPendingUpdate();
    const availableRow = document.getElementById('settings-update-available');
    const availableLabel = document.getElementById('settings-update-available-label');
    const changelogLink = document.getElementById('settings-changelog-link');
    const checkBtn = document.getElementById('settings-check-updates-btn');
    const updateBtn = document.getElementById('settings-apply-update-btn');
    const forceBtn = document.getElementById('settings-force-update-btn');
    if (pending) {
        if (availableLabel) {
            availableLabel.textContent = `Update available: ${pending.version}`;
        }
        if (changelogLink) {
            changelogLink.href = changelogUrlForUpdate(pending);
        }
        setHidden(availableRow, false);
        setHidden(checkBtn, true);
        setHidden(updateBtn, false);
        setHidden(forceBtn, true);
    }
    else {
        setHidden(availableRow, true);
        setHidden(checkBtn, false);
        setHidden(updateBtn, true);
        setHidden(forceBtn, !shouldShowForceUpdate(pending, forceUpdateOffered));
    }
    const settingsBtn = document.getElementById('settings-btn');
    if (settingsBtn) {
        settingsBtn.classList.toggle('has-update', !!pending);
    }
}
async function fetchPublishedSwVersion() {
    const response = await fetch(`coi-serviceworker.js?t=${Date.now()}`, {
        cache: 'no-store'
    });
    if (!response.ok) {
        throw new Error(`Could not fetch service worker (${response.status})`);
    }
    return parseSwVersions(await response.text());
}
function prefersPreviewReleases() {
    return window.location.hostname.includes('preview');
}
async function fetchPublishedGitHubVersion() {
    const response = await fetch(GITHUB_RELEASES_URL, { cache: 'no-store' });
    if (!response.ok) {
        throw new Error(`Could not fetch GitHub releases (${response.status})`);
    }
    return pickLatestGitHubRelease((await response.json()), prefersPreviewReleases());
}
export function pickLatestGitHubRelease(releases, preview) {
    const release = releases.find((item) => {
        if (!item.tag_name || item.draft) {
            return false;
        }
        return preview ? !!item.prerelease : !item.prerelease;
    });
    if (!release?.tag_name) {
        return null;
    }
    const displayVersion = release.tag_name;
    return {
        tag: release.tag_name,
        displayVersion,
        isPreview: !!release.prerelease ||
            isPreviewReleaseId(release.tag_name, displayVersion)
    };
}
async function resolvePublishedVersion() {
    const runningDisplay = getRunningDisplayVersion();
    const runningTag = getRunningTag();
    const [fromSw, fromGitHub] = await Promise.all([
        fetchPublishedSwVersion().catch((error) => {
            console.warn('Published service worker lookup failed:', error instanceof Error ? error.message : String(error));
            return null;
        }),
        fetchPublishedGitHubVersion().catch((error) => {
            console.warn('GitHub releases lookup failed:', error instanceof Error ? error.message : String(error));
            return null;
        })
    ]);
    if (fromSw && hasAvailableUpdate(fromSw, runningDisplay, runningTag)) {
        return fromSw;
    }
    if (fromGitHub &&
        hasAvailableUpdate(fromGitHub, runningDisplay, runningTag)) {
        return fromGitHub;
    }
    return fromSw || fromGitHub;
}
export function isAutoCheckThrottled(lastCheckAt, now = Date.now()) {
    return lastCheckAt > 0 && now - lastCheckAt < AUTO_CHECK_THROTTLE_MS;
}
function shouldSkipScheduledChecks() {
    if (typeof window === 'undefined') {
        return true;
    }
    if (window.isTestMode?.() || window.isDevelopment?.()) {
        return true;
    }
    return !navigator.serviceWorker;
}
function isDevBuild(displayVersion) {
    return displayVersion.endsWith('-dev');
}
async function checkForUpdates(userInitiated = false) {
    if (checkInFlight && !userInitiated) {
        return;
    }
    checkInFlight = true;
    lastAutoCheckAt = Date.now();
    const statusEl = document.getElementById('settings-update-status');
    const checkBtn = document.getElementById('settings-check-updates-btn');
    const forceBtn = document.getElementById('settings-force-update-btn');
    if (userInitiated && statusEl) {
        statusEl.textContent = 'Checking for updates…';
    }
    if (userInitiated && checkBtn) {
        checkBtn.disabled = true;
    }
    if (userInitiated && forceBtn) {
        forceBtn.disabled = true;
    }
    try {
        const registration = navigator.serviceWorker
            ? await navigator.serviceWorker.getRegistration()
            : undefined;
        const updatePromise = (registration?.update() ?? Promise.resolve()).catch((error) => {
            console.warn('Service worker update() failed:', error instanceof Error ? error.message : String(error));
        });
        const publishedPromise = resolvePublishedVersion();
        await Promise.race([
            updatePromise,
            new Promise((resolve) => {
                window.setTimeout(resolve, 8000);
            })
        ]);
        const published = await publishedPromise;
        const runningDisplay = getRunningDisplayVersion();
        const runningTag = getRunningTag();
        const waitingWorker = !!registration?.waiting;
        if (published &&
            !isDevBuild(runningDisplay) &&
            (hasAvailableUpdate(published, runningDisplay, runningTag) ||
                waitingWorker)) {
            setPendingUpdate(pendingUpdateFromSw(published));
            if (statusEl) {
                statusEl.textContent = '';
            }
            return;
        }
        if (userInitiated && statusEl) {
            statusEl.textContent = getPendingUpdate()
                ? ''
                : 'You’re up to date.';
        }
        if (userInitiated && !getPendingUpdate()) {
            forceUpdateOffered = true;
        }
    }
    catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.warn('Update check failed:', message);
        if (userInitiated && statusEl) {
            statusEl.textContent = 'Could not check for updates.';
        }
        if (userInitiated) {
            forceUpdateOffered = true;
        }
    }
    finally {
        checkInFlight = false;
        if (checkBtn) {
            checkBtn.disabled = false;
        }
        const forceBtn = document.getElementById('settings-force-update-btn');
        if (forceBtn) {
            forceBtn.disabled = forceReinstallInFlight;
        }
        refreshPreferencesVersionUi();
    }
}
function runScheduledUpdateCheck(bypassThrottle) {
    if (checkInFlight) {
        return;
    }
    if (!bypassThrottle && isAutoCheckThrottled(lastAutoCheckAt)) {
        return;
    }
    void checkForUpdates(false);
}
function initScheduledUpdateChecks() {
    if (scheduledChecksInitialized || shouldSkipScheduledChecks()) {
        return;
    }
    scheduledChecksInitialized = true;
    runScheduledUpdateCheck(false);
    // macOS and installed PWAs often skip window `focus` / visibilitychange on
    // Cmd+Tab. document.hasFocus() is the reliable signal (same as glyph-canvas);
    // poll so a missed event still produces an inactive → active edge.
    let appWindowWasActive = isAppWindowActive();
    const syncAppWindowAndCheck = () => {
        const active = isAppWindowActive();
        if (didAppWindowBecomeActive(appWindowWasActive, active)) {
            appWindowWasActive = active;
            runScheduledUpdateCheck(false);
            return;
        }
        appWindowWasActive = active;
    };
    window.addEventListener('focus', syncAppWindowAndCheck);
    window.addEventListener('blur', syncAppWindowAndCheck);
    window.addEventListener('pageshow', syncAppWindowAndCheck);
    window.addEventListener('pagehide', syncAppWindowAndCheck);
    document.addEventListener('visibilitychange', syncAppWindowAndCheck);
    document.addEventListener('freeze', syncAppWindowAndCheck);
    document.addEventListener('resume', syncAppWindowAndCheck);
    window.setInterval(syncAppWindowAndCheck, FOCUS_POLL_MS);
    window.setInterval(() => {
        runScheduledUpdateCheck(true);
    }, AUTO_CHECK_INTERVAL_MS);
}
function applyPendingUpdate() {
    const reload = () => {
        window.location.reload();
    };
    if (!navigator.serviceWorker) {
        reload();
        return;
    }
    void navigator.serviceWorker.getRegistration().then((registration) => {
        if (!registration?.waiting) {
            reload();
            return;
        }
        navigator.serviceWorker.addEventListener('controllerchange', reload, {
            once: true
        });
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.setTimeout(reload, 2000);
    });
}
function stripUpdateQuery() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(UPDATE_QUERY)) {
        return;
    }
    url.searchParams.delete(UPDATE_QUERY);
    window.history.replaceState(window.history.state, '', url.toString());
}
/**
 * Drop the service worker and Cache Storage, then reload from the network.
 * Used when version comparison reports no update but the running shell may
 * still be a stale cached build.
 */
async function forceReinstallFromNetwork() {
    if (forceReinstallInFlight) {
        return;
    }
    forceReinstallInFlight = true;
    const statusEl = document.getElementById('settings-update-status');
    const checkBtn = document.getElementById('settings-check-updates-btn');
    const forceBtn = document.getElementById('settings-force-update-btn');
    if (statusEl) {
        statusEl.textContent = 'Reinstalling from the network…';
    }
    if (checkBtn) {
        checkBtn.disabled = true;
    }
    if (forceBtn) {
        forceBtn.disabled = true;
    }
    try {
        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
        }
        if (navigator.serviceWorker) {
            const registration = await navigator.serviceWorker.getRegistration();
            if (registration?.waiting) {
                registration.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
            await registration?.unregister();
        }
    }
    catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.warn('Force update failed:', message);
    }
    const url = new URL(window.location.href);
    url.searchParams.set(UPDATE_QUERY, String(Date.now()));
    window.location.replace(url.toString());
}
export function initPreferencesVersionUi() {
    const skipUrlReinstall = !!window.isTestMode?.();
    if (!skipUrlReinstall && shouldForceReinstallFromUrl()) {
        void forceReinstallFromNetwork();
    }
    else {
        stripUpdateQuery();
    }
    refreshPreferencesVersionUi();
    initScheduledUpdateChecks();
    if (preferencesUiInitialized) {
        return;
    }
    preferencesUiInitialized = true;
    document
        .getElementById('settings-check-updates-btn')
        ?.addEventListener('click', () => {
        void checkForUpdates(true);
    });
    document
        .getElementById('settings-apply-update-btn')
        ?.addEventListener('click', () => {
        applyPendingUpdate();
    });
    document
        .getElementById('settings-force-update-btn')
        ?.addEventListener('click', () => {
        void forceReinstallFromNetwork();
    });
    document.addEventListener('counterpunch:update-available', () => {
        const statusEl = document.getElementById('settings-update-status');
        if (statusEl && getPendingUpdate()) {
            statusEl.textContent = '';
        }
        refreshPreferencesVersionUi();
    });
}
if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPreferencesVersionUi);
    }
    else {
        initPreferencesVersionUi();
    }
}
